import { useEffect, useRef, RefObject } from "react";

/**
 * Returns a ref that toggles the `inert` DOM attribute based on the `active` flag.
 * Useful for wrapping AnimatePresence so exiting content is not focusable.
 *
 * React 17 does not support `inert` as a JSX prop, so this uses
 * setAttribute/removeAttribute directly on the DOM element.
 *
 * @param active - When true, removes inert (content is interactive).
 *                 When false, sets inert (content is non-interactive).
 * @returns Attach to a wrapper div around AnimatePresence.
 */
export default function useInertRef(active: boolean): RefObject<HTMLElement | null> {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (active) {
      ref.current.removeAttribute("inert");
    } else {
      ref.current.setAttribute("inert", "");
    }
  }, [active]);

  return ref;
}
